/**
 * One makeStyles sheet for every Connect Splyt step. Same recipe as the join
 * pages: marsh page, white card on desktop, pinned footer on mobile, caviar
 * pill button, radii.input wells.
 */
import { makeStyles } from '@material-ui/core/styles';
import {
  brand,
  fontSans,
  spacing,
  radii,
  shadows,
} from '../../style/splytBrand';

const useConnectStyles = makeStyles({
  root: {
    minHeight: '100vh',
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    backgroundColor: brand.marsh,
    fontFamily: fontSans,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },

  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    minHeight: '100vh',
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    padding: `${spacing.md}px ${spacing.md + 4}px 0`,
  },

  mobileHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 22,
  },

  desktopWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100vh',
    boxSizing: 'border-box',
    padding: '24px 40px 40px',
  },

  desktopHeader: {
    width: '100%',
    maxWidth: 1080,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 28,
  },

  desktopCard: {
    width: '100%',
    maxWidth: 480,
    boxSizing: 'border-box',
    backgroundColor: brand.surface,
    borderRadius: radii.card,
    padding: 36,
    boxShadow: shadows.card,
    border: `1px solid ${brand.marshNeutral}`,
  },

  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    paddingBottom: spacing.md,
  },

  desktopContent: {
    display: 'flex',
    flexDirection: 'column',
  },

  desktopFooter: {
    marginTop: spacing.lg,
  },

  wordmark: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 22,
    letterSpacing: '-0.04em',
    color: brand.caviar,
  },

  title: {
    margin: 0,
    fontWeight: 700,
    letterSpacing: '-0.025em',
    color: brand.caviar,
    overflowWrap: 'anywhere',
  },

  titleMobile: {
    fontSize: 26,
    lineHeight: 1.1,
  },

  titleDesktop: {
    fontSize: 30,
    lineHeight: 1.1,
  },

  subtitle: {
    margin: '6px 0 22px',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.45,
    color: brand.caviar75,
  },

  phoneNumber: {
    fontWeight: 600,
    color: brand.caviar,
    fontVariantNumeric: 'tabular-nums',
  },

  // Phone input row
  phoneInputRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 8,
  },

  countryPicker: {
    appearance: 'none',
    cursor: 'default',
    flexShrink: 0,
    backgroundColor: brand.surface,
    border: `1.5px solid ${brand.marshNeutral}`,
    borderRadius: radii.input,
    padding: '0 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: fontSans,
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1,
    color: brand.caviar,
  },

  countryFlag: {
    fontSize: 22,
  },

  // Input well, shared by the phone and name fields
  inputWrapper: {
    minWidth: 0,
    backgroundColor: brand.surface,
    borderRadius: radii.input,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  },

  // Only inside the phone row: fills the width left by the country chip.
  phoneInputWrapper: {
    flex: 1,
  },

  inputWrapperDefault: {
    border: `1.5px solid ${brand.marshNeutral}`,
  },

  inputWrapperFocused: {
    border: `1.5px solid ${brand.caviar}`,
    boxShadow: shadows.inputFocus,
  },

  textInput: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    border: 'none',
    outline: 'none',
    fontFamily: fontSans,
    fontWeight: 500,
    fontSize: 17,
    lineHeight: 1,
    color: brand.caviar,
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: brand.caviar50,
    },
  },

  tabularInput: {
    fontVariantNumeric: 'tabular-nums',
  },

  fieldLabel: {
    margin: '22px 0 8px',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.2,
    color: brand.caviar,
  },

  fieldHint: {
    margin: '8px 0 0',
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.45,
    color: brand.caviar50,
  },

  // Code boxes
  codeBoxesContainer: {
    display: 'flex',
    gap: 10,
    position: 'relative',
  },

  codeBoxesShake: {
    animation: '$shake 200ms ease-in-out',
  },

  '@keyframes shake': {
    '0%, 100%': { transform: 'translateX(0)' },
    '12.5%': { transform: 'translateX(-6px)' },
    '37.5%': { transform: 'translateX(6px)' },
    '62.5%': { transform: 'translateX(-6px)' },
    '87.5%': { transform: 'translateX(6px)' },
  },

  codeBox: {
    flex: 1,
    aspectRatio: '1 / 1.15',
    backgroundColor: brand.surface,
    borderRadius: radii.input,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1,
    color: brand.caviar,
    fontVariantNumeric: 'tabular-nums',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    cursor: 'text',
  },

  codeBoxEmpty: {
    border: `1.5px solid ${brand.marshNeutral}`,
  },

  codeBoxFilled: {
    border: `1.5px solid ${brand.caviar25}`,
  },

  codeBoxActive: {
    border: `1.5px solid ${brand.caviar}`,
    boxShadow: shadows.inputFocus,
  },

  hiddenInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'text',
    fontSize: 16,
  },

  cursor: {
    width: 2,
    height: 28,
    backgroundColor: brand.caviar,
    animation: '$blink 1s steps(2, end) infinite',
  },

  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },

  emptyPlaceholder: {
    width: 14,
    height: 2,
    backgroundColor: brand.caviar25,
    borderRadius: 1,
  },

  metaRow: {
    marginTop: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  timerText: {
    fontWeight: 400,
    fontSize: 13,
    lineHeight: 1,
    color: brand.caviar50,
  },

  timerCount: {
    fontWeight: 600,
    color: brand.caviar,
    fontVariantNumeric: 'tabular-nums',
  },

  inlineLink: {
    color: brand.blueberry,
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    borderBottom: `1.5px solid ${brand.blueberry25}`,
    padding: 0,
    paddingBottom: 1,
    cursor: 'pointer',
    fontFamily: fontSans,
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  },

  // Consent step
  hostLine: {
    margin: '6px 0 0',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.45,
    color: brand.caviar75,
  },

  hostName: {
    fontWeight: 600,
    color: brand.caviar,
  },

  signedInLine: {
    margin: '4px 0 0',
    fontWeight: 400,
    fontSize: 13,
    lineHeight: 1.45,
    color: brand.caviar50,
  },

  scopeWell: {
    marginTop: 22,
    padding: '14px 16px',
    backgroundColor: brand.marsh75,
    borderRadius: radii.lg,
    border: `1px solid ${brand.marshNeutral}`,
  },

  scopeHeading: {
    margin: '0 0 10px',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
    color: brand.caviar,
  },

  scopeList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },

  scopeItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.45,
    color: brand.caviar75,
  },

  scopeMark: {
    flexShrink: 0,
    marginTop: 3,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: brand.caviar,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Privacy note (phone step)
  privacyNote: {
    marginTop: 18,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 14px',
    backgroundColor: brand.marsh75,
    borderRadius: radii.lg,
    border: `1px solid ${brand.marshNeutral}`,
  },

  privacyEmoji: {
    fontSize: 16,
    lineHeight: 1,
    marginTop: 1,
  },

  privacyText: {
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.45,
    color: brand.caviar75,
  },

  // Buttons
  primaryButton: {
    width: '100%',
    backgroundColor: brand.caviar,
    color: brand.surface,
    border: 'none',
    borderRadius: radii.pill,
    padding: '16px 22px',
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.1,
    letterSpacing: '-0.005em',
    boxShadow: shadows.primaryButton,
    cursor: 'pointer',
    transition: 'transform 120ms ease, opacity 150ms ease',
    '&:hover:not(:disabled)': {
      transform: 'scale(0.985)',
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      backgroundColor: brand.caviar25,
      boxShadow: 'none',
      cursor: 'not-allowed',
    },
  },

  textButton: {
    display: 'block',
    width: '100%',
    marginTop: 10,
    padding: '12px 22px',
    background: 'none',
    border: 'none',
    fontFamily: fontSans,
    fontWeight: 600,
    fontSize: 15,
    lineHeight: 1.1,
    color: brand.caviar75,
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      color: brand.caviar,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },

  caption: {
    margin: '14px 0 0',
    textAlign: 'center',
    fontWeight: 400,
    fontSize: 11,
    lineHeight: 1.45,
    color: brand.caviar50,
  },

  captionLink: {
    color: brand.caviar75,
    textDecoration: 'underline',
    cursor: 'pointer',
  },

  captionUpper: {
    textTransform: 'uppercase',
  },

  errorMessage: {
    marginTop: 16,
    padding: '12px 14px',
    backgroundColor: `${brand.ketchup}15`,
    borderRadius: radii.lg,
    fontWeight: 500,
    fontSize: 13,
    lineHeight: 1.4,
    color: brand.ketchup,
    textAlign: 'center',
  },

  // Loading and expired states
  stateWrap: {
    minHeight: '100vh',
    '@supports (height: 100svh)': { minHeight: '100svh' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 32px',
  },

  spinner: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: `3px solid ${brand.marshNeutral}`,
    borderTopColor: brand.caviar,
    animation: '$spin 800ms linear infinite',
  },

  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' },
  },

  stateWordmark: {
    marginBottom: 28,
  },

  stateTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    color: brand.caviar,
  },

  stateText: {
    margin: '10px 0 0',
    maxWidth: 320,
    fontWeight: 400,
    fontSize: 15,
    lineHeight: 1.5,
    color: brand.caviar75,
  },
});

export default useConnectStyles;
